import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';

import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';
import cn from 'classnames';

import Opener from '../../components/Opener';
import Input from '../input'; // eslint-disable-line
import './styles.scss';

/**
 * ColorPicker
 * @param {Object} props
 * @param {String} props.initialColor
 * @param {Function} props.onColorChange - live preview while dragging (optional)
 * @param {Function} props.onColorChangeComplete
 * @param {Boolean} props.disabled
 * @param {Boolean} props.commitOnPickerClose - commit on opener close instead of each drag mouse-up
 * @returns {JSX}
 */
export default function ColorPicker({
  initialColor,
  onColorChange,
  onColorChangeComplete,
  disabled,
  error,
  commitOnPickerClose = false,
}) {
  const [color, setColor] = useState(initialColor || '#fff');
  const [isColorPickerOpen, setColorPickerOpen] = useState(false);
  const wasPickerOpenRef = useRef(false);

  useEffect(() => {
    if (commitOnPickerClose && isColorPickerOpen) return;
    if (initialColor !== color) {
      setColor(initialColor);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialColor, isColorPickerOpen, commitOnPickerClose]);

  useEffect(() => {
    if (wasPickerOpenRef.current && !isColorPickerOpen && commitOnPickerClose) {
      onColorChangeComplete(color);
    }
    wasPickerOpenRef.current = isColorPickerOpen;
  }, [isColorPickerOpen, color, commitOnPickerClose, onColorChangeComplete]);

  const handleColorChange = useCallback((nextColor) => {
    const { hex } = nextColor;
    setColor(hex);
    if (onColorChange) onColorChange(hex);
  }, [onColorChange]);

  const handlePickerChangeComplete = useCallback((nextColor) => {
    setColor(nextColor.hex);
    if (!commitOnPickerClose) {
      onColorChangeComplete(nextColor.hex);
    }
  }, [commitOnPickerClose, onColorChangeComplete]);

  const handleColorBlur = useCallback(() => {
    onColorChangeComplete(color);
  }, [color, onColorChangeComplete]);

  return (
    <div className={cn('colorPicker', { isColorPickerOpen, isDisabled: disabled })}>
      <Input
        value={color}
        error={error}
        onChange={(e, value) => handleColorChange({ hex: value })}
        onBlur={handleColorBlur}
      />
      <div className="colorPickerValue" style={{ backgroundColor: color }} />
      <Opener
        hideOnClickOutside
        openerText="Open colorPicker"
        hideOpenerWhenOpen={false}
        parentHandler={setColorPickerOpen}
        additionalClassName="openerLink"
      >
        <span>
          <ChromePicker
            color={color}
            disableAlpha
            onChange={handleColorChange}
            onChangeComplete={handlePickerChangeComplete}
          />
        </span>
      </Opener>
    </div>
  );
}

ColorPicker.propTypes = {
  initialColor: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  commitOnPickerClose: PropTypes.bool,
  onColorChange: PropTypes.func,
  onColorChangeComplete: PropTypes.func.isRequired,
};

ColorPicker.defaultProps = {
  initialColor: '#fff',
  disabled: false,
  error: null,
  onColorChange: null,
  commitOnPickerClose: false,
};
